"use client"

import * as React from "react"
const { useState, useEffect, useRef } = React
// import React, { useState, useEffect, useRef } from "react"
// import { useState } from 'react'
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import {
  EyeIcon,
  EyeOffIcon,
  XIcon,
  Rocket,
  Zap,
  Code,
  Cpu,
  Database,
  Wifi,
  Cog,
  Terminal,
  Bot,
  Loader2,
} from "lucide-react"
import apiClient from "../../utils/apiClient"
import activityTracker from '../../utils/activityTracker';

// import { toast } from 'react-hot-toast';

const FloatingParticles = () => {
  const icons = [Zap, Code, Cpu, Database, Wifi, Cog, Terminal, Bot]

  return (
    <div className="absolute inset-0 overflow-hidden">
      {icons.map((Icon, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            //   top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, Math.random() * 100 - 30],
            x: [0, Math.random() * 100 - 50],
            scale: [1, Math.random() + 0.5, 1],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: Math.random() * 5 + 5,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: "reverse",
          }}
        >
          <Icon className="h-6 w-6 text-blue-500" /> {/* Adjust size/color if needed */}
        </motion.div>
      ))}
    </div>
  )
}

const otpStyles = {
  otpBox: {
    width: "3rem",
    height: "3rem",
    fontSize: "1.5rem",
    textAlign: "center",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },
  otpInputContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "1rem",
  },
  otpButton: {
    backgroundColor: "#007bff",
    color: "#fff",
    padding: "0.75rem 1.5rem",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontSize: "1rem",
  },
  otpForm: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  otpContainer: {
    margin: "20px",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
}

const validateEmail = (email) => {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return re.test(email)
}

const validatePassword = (password) => {
  const minLength = 8
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasDigit = /\d/.test(password)
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)

  return password.length >= minLength && hasUpperCase && hasLowerCase && hasDigit && hasSpecialChar
}

export default function LoginDropdown({ isOpen, onClose }) {
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState("")
  const [registerError, setRegisterError] = useState("")
  const [otpError, setOtpError] = useState("")
  const [otpAuth, setOtpAuth] = useState(false)
  const [loginEmail, setLoginEmail] = useState("")
  const [loginPassword, setLoginPassword] = useState("")
  const [signupEmail, setSignupEmail] = useState("")
  const [signupPassword, setSignupPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [signupName, setSignupName] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("")
  const [forgotPasswordError, setForgotPasswordError] = useState("")
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [isResetPassword, setIsResetPassword] = useState(false)
  const [resetToken, setResetToken] = useState("")
  const [isSendingReset, setIsSendingReset] = useState(false)
  const [buttonText, setButtonText] = useState("Send Reset Link")
  const [otp, setOtp] = useState(new Array(6).fill(""))
  const inputRefs = useRef([])
  const [otpTime, setOtpTime] = useState(60) // 2 min for otp resend
  const [isResendActive, setIsResendActive] = useState(false)
  const [resentOtp, setResentOtp] = useState(false)
  const otpTimerRef = useRef(null)
  const [showUserDetails, setShowUserDetails] = useState(false)
  const [dateOfBirth, setDateOfBirth] = useState("")
  const [gender, setGender] = useState("")
  const [country, setCountry] = useState("")
  const [userDetailsError, setUserDetailsError] = useState("")
  const [registrationSuccess, setRegistrationSuccess] = useState(false)

  const startTimer = () => {
    setOtpTime(60) // Reset timer
    setIsResendActive(false)
    setResentOtp(false)
    clearInterval(otpTimerRef.current) // Clear any existing timer

    otpTimerRef.current = setInterval(() => {
      setOtpTime((prev) => {
        if (prev <= 1) {
          clearInterval(otpTimerRef.current)
          setIsResendActive(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  useEffect(() => {
    let isMounted = true;
    if (otpAuth && isMounted) {
      startTimer()
      return
    }
    return () => isMounted = false;
  }, [otpAuth])

  // Clear timer on component unmount (optional safety)
  useEffect(() => {
    return () => clearInterval(otpTimerRef.current)
  }, [])

  const formatTime = (seconds) => {
    const min = String(Math.floor(seconds / 60)).padStart(2, "0")
    const sec = String(seconds % 60).padStart(2, "0")
    return `${min}:${sec}`
  }

  const handleOtpResend = async (e) => {
    e.preventDefault()

    if (!resentOtp && isResendActive) {
      setResentOtp(true)
      setIsResendActive(false)

      setIsLoading(true)

      try {
        const response = await apiClient.post("/auth/resend-otp", {
          name: signupName,
          email: signupEmail,
          password: signupPassword,
        })

        if (response.status === 200) {
          console.log("Otp Resent")
        }
      } catch (error) {
        setOtpError(error.response?.data?.error || "Registration failed")
        console.error("Registration error:", error)
      } finally {
        startTimer()
        setResentOtp(false)
        setIsLoading(false)
      }
    }
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault()

    if (!validateEmail(signupEmail)) {
      setRegisterError("Please enter a valid email address")
      return
    }

    if (!validatePassword(signupPassword)) {
      setRegisterError(
        "Password must be at least 8 characters long, contain at least one uppercase letter, one lowercase letter, one digit, and one special character",
      )
      return
    }

    if (signupPassword !== confirmPassword) {
      setRegisterError("Passwords do not match")
      return
    }

    if (!signupName.trim()) {
      setRegisterError("Please enter your name")
      return
    }

    setRegisterError("") // Clear any previous errors
    setIsLoading(true)

    try {
      const response = await apiClient.post("/auth/register", {
        name: signupName,
        email: signupEmail,
        password: signupPassword,
      })

      if (response.status === 200) {
        setOtpAuth(true)
      }
    } catch (error) {
      setRegisterError(error.response?.data?.error || "Registration failed")
      console.error("Registration error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmit = async (e) => {
    e.preventDefault()
    setOtpError("")
    setIsLoading(true)
    try {
      const submittedOtp = otp.join("")
      const response = await apiClient.post("/auth/otp-validation", {
        email: signupEmail,
        submittedOtp: submittedOtp,
      })
      if (response.status === 201) {
        setOtpAuth(false)
        setShowUserDetails(true)
      }
    } catch (error) {
      setOtpError(error.response?.data?.error || "Authentication failed")
      console.error("Authentication error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // To make sure it runs on old browsers too
  const generateSessionId = () =>
    'sid_' + [...crypto.getRandomValues(new Uint8Array(32))].map(x => x.toString(16).padStart(2, 0)).join('')

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
        const response = await apiClient.post("/auth/login", {
            email: loginEmail,
            password: loginPassword,
        });

        if (response.data.accessToken) {
            // Store tokens and user data
            localStorage.setItem("accessToken", response.data.accessToken);
            localStorage.setItem("userId", response.data.userId);
            if (response.data.role) {
                localStorage.setItem("userRole", response.data.role);
            }
            if (response.data.accessibleModules) {
                localStorage.setItem("accessibleModules", JSON.stringify(response.data.accessibleModules));
            }
            const sessionId = generateSessionId()
            localStorage.setItem("sessionId", sessionId);

            // Create initial activity record if not exists
            try {
                await apiClient.post(`/api/users/${response.data.userId}/activity/init`, {
                    userId: response.data.userId,
                    date: new Date(),
                    screenTime: 0
                });
            } catch (activityError) {
                console.error("Error initializing activity:", activityError);
            }

            //saving session in backend
            try {
              apiClient.post("/auth/session", {
                email: loginEmail, 
                sessionId
              });
            } catch (err){
              console.error("Error while saving session", err)
            }

            // Dispatch new event to update login sessions on login
            window.dispatchEvent(new Event('authDataUpdated'));

            // Start activity tracking
            activityTracker.startTracking();

            // Dispatch login event
            window.dispatchEvent(new Event("login-state-changed"));

            // Close modal
            onClose();

            // If user is Admin, route to Admin landing page
            if (response.data.role === 'admin') {
                window.location.hash = '#admin';
                window.dispatchEvent(new CustomEvent('open-admin-dashboard', { detail: { open: true } }));
            }
        }
    } catch (error) {
        setLoginError(error.response?.data?.error || "Login failed");
        console.error("Login error:", error);
    } finally {
        setIsLoading(false);
    }
};

  const handleOtpInputChange = (e, index) => {
    const value = e.target.value
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1)
    setOtp(newOtp)

    if (value && index < 5) {
      inputRefs.current[index + 1].focus()
    }
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData("text").trim()
    if (!/^\d{6}$/.test(pasteData)) return

    const newOtp = pasteData.split("")
    setOtp(newOtp)
    // Focus last field
    const lastIndex = newOtp.length - 1
    if (inputRefs.current[lastIndex]) {
      inputRefs.current[lastIndex].focus()
    }
  }
  const handleOtpInputKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus()
    }
  }

  const handleForgotPassword = (e) => {
    e.preventDefault()
    setIsForgotPassword(true)
  }

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault()
    setForgotPasswordError("")
    setIsSendingReset(true)
    console.log("Sending request...")

    try {
      const response = await apiClient.post("/auth/forgot-password", {
        email: forgotPasswordEmail,
      })

      if (response.status === 200) {
        setButtonText("Sent!")
        setTimeout(() => {
          setIsForgotPassword(false)
          setButtonText("Send Reset Link") // Reset text when modal closes
        }, 1000)
      }
    } catch (error) {
      console.log("Error:", error)
      setForgotPasswordError(error.response?.data?.error || "Failed to send reset email")
    } finally {
      setIsSendingReset(false)
      // alert('Email sent!');
    }
  }

  const handleResetLinkClick = (token) => {
    setResetToken(token)
    setIsResetPassword(true)
  }

  const handleUserDetailsSubmit = async (e) => {
    e.preventDefault()
    setUserDetailsError("")
    setIsLoading(true)

    try {
      const response = await apiClient.post("/auth/complete-registration", {
        email: signupEmail,
        dateOfBirth,
        gender,
        country: "India", // Always send India
      })

      if (response.status === 200) {
        setRegistrationSuccess(true)
        setTimeout(() => {
          onClose()
          window.dispatchEvent(new Event("registration-complete"))
        }, 3000)
      }
    } catch (error) {
      setUserDetailsError(error.response?.data?.error || "Failed to complete registration")
    } finally {
      setIsLoading(false)
    }
  }

  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  }

  // Add cleanup on component unmount
  useEffect(() => {
    return () => {
      if (!isOpen) {
        activityTracker.stopTracking();
      }
    };
  }, [isOpen]);

  return (
    <div className="fixed inset-0 bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-lg bg-white/90 backdrop-blur-md shadow-xl relative overflow-hidden">
        {/* <FloatingParticles /> */}
        {!otpAuth ? (
          <div>
            <CardHeader className="relative z-10">
              <CardTitle className="text-3xl font-bold text-center bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-600 bg-clip-text text-transparent flex items-center justify-center">
                <span className="text-orange-400">STEM</span> <Rocket className="mx-2 h-8 w-8 text-red-500" />{" "}
                <span className="text-green-400">Verse</span>
              </CardTitle>
              <CardDescription className="text-center text-gray-600">
                Sign in to your account or create a new one
              </CardDescription>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
                onClick={onClose}
              >
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </CardHeader>
            <CardContent className="relative z-10">
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger
                    value="login"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-500 data-[state=active]:text-white"
                  >
                    Login
                  </TabsTrigger>
                  <TabsTrigger
                    value="register"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-500 data-[state=active]:text-white"
                  >
                    Register
                  </TabsTrigger>
                </TabsList>
                <AnimatePresence mode="wait">
                  <TabsContent value="login" key="login">
                    <motion.form
                      id="login-form"
                      onSubmit={handleLoginSubmit}
                      className="space-y-4"
                      variants={formVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-700">
                          Email
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="Enter your email"
                          required
                          className="bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-gray-700">
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            required
                            className="bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOffIcon className="h-4 w-4 text-gray-500" />
                            ) : (
                              <EyeIcon className="h-4 w-4 text-gray-500" />
                            )}
                            <span className="sr-only">Toggle password visibility</span>
                          </Button>
                        </div>
                      </div>
                      {loginError && <p className="text-sm text-red-500">{loginError}</p>}
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                      >
                        Sign In
                      </Button>
                      <p className="text-sm text-gray-600 text-center">
                        <a
                          href="#"
                          onClick={handleForgotPassword}
                          className="text-blue-500 hover:text-blue-700 underline underline-offset-4"
                        >
                          Forgot Password?
                        </a>
                      </p>
                    </motion.form>
                  </TabsContent>
                  <TabsContent value="register" key="register">
                    <motion.form
                      id="register-form"
                      onSubmit={handleRegisterSubmit}
                      className="space-y-4"
                      variants={formVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-700">
                          Name
                        </Label>
                        <Input
                          id="name"
                          placeholder="Enter your full name"
                          required
                          className="bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400"
                          value={signupName}
                          onChange={(e) => setSignupName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-email" className="text-gray-700">
                          Email
                        </Label>
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="Enter your email"
                          required
                          className="bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400"
                          value={signupEmail}
                          onChange={(e) => setSignupEmail(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-password" className="text-gray-700">
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="register-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Create a password"
                            required
                            className="bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400"
                            value={signupPassword}
                            onChange={(e) => setSignupPassword(e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOffIcon className="h-4 w-4 text-gray-500" />
                            ) : (
                              <EyeIcon className="h-4 w-4 text-gray-500" />
                            )}
                            <span className="sr-only">Toggle password visibility</span>
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password" className="text-gray-700">
                          Confirm Password
                        </Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          placeholder="Confirm your password"
                          required
                          className="bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                      </div>
                      {registerError && <p className="text-sm text-red-500">{registerError}</p>}
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                      >
                        Register
                      </Button>
                    </motion.form>
                  </TabsContent>
                </AnimatePresence>
              </Tabs>
            </CardContent>
            <CardFooter className="flex justify-center">
              <p className="text-sm text-gray-600">
                By continuing, you agree to our{" "}
                <a href="#" className="text-blue-500 hover:text-blue-700 underline underline-offset-4">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="#" className="text-blue-500 hover:text-blue-700 underline underline-offset-4">
                  Privacy Policy
                </a>
                .
              </p>
            </CardFooter>
          </div>
        ) : (
          <div style={otpStyles.otpContainer}>
            <button
              onClick={() => setOtpAuth(false)}
              className="absolute top-3 left-4 flex items-center gap-1 text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0  24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <h2>Enter OTP</h2>
            <p>We've sent a 6-digit code to your email</p>
            <form onSubmit={handleOtpSubmit} style={otpStyles.otpForm}>
              <div style={otpStyles.otpInputContainer}>
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    type="text"
                    maxLength={1}
                    value={digit}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    onChange={(e) => handleOtpInputChange(e, idx)}
                    onKeyDown={(e) => handleOtpInputKeyDown(e, idx)}
                    onPaste={handleOtpPaste}
                    style={otpStyles.otpBox}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
              <button type="submit" style={otpStyles.otpButton}>
                Verify
              </button>
              {otpError && <p className="text-sm text-red-500">{otpError}</p>}
            </form>
            <div className="mt-4">
              {!isResendActive ? (
                <p className="text-gray-500">
                  Resend OTP in: <strong>{formatTime(otpTime)}</strong>
                </p>
              ) : (
                <button
                  onClick={handleOtpResend}
                  disabled={resentOtp}
                  className={`text-sm font-medium transition-colors underline-offset-2 
                      ${resentOtp ? "text-gray-400 cursor-not-allowed" : "text-blue-600 hover:text-blue-700 underline"}`}
                >
                  {resentOtp ? "OTP Sent" : "Resend OTP"}
                </button>
              )}
            </div>
          </div>
        )}
      </Card>
      {isForgotPassword && (
        <div className="fixed inset-0 bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md bg-white/90 backdrop-blur-md shadow-xl relative overflow-hidden">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-center">Forgot Password</CardTitle>
              <CardDescription className="text-center text-gray-600">
                Enter your email to receive a password reset link.
              </CardDescription>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
                onClick={() => setIsForgotPassword(false)}
              >
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-password-email" className="text-gray-700">
                    Email
                  </Label>
                  <Input
                    id="forgot-password-email"
                    type="email"
                    placeholder="Enter your email"
                    required
                    className="bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  />
                </div>
                {forgotPasswordError && <p className="text-sm text-red-500">{forgotPasswordError}</p>}
                <Button
                  type="submit"
                  disabled={isSendingReset}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white flex items-center justify-center"
                >
                  {isSendingReset ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    buttonText
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      {showUserDetails && (
        <div className="fixed inset-0 bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-sm bg-white/90 backdrop-blur-md shadow-xl relative overflow-hidden">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-center">You're Almost There!</CardTitle>
              <CardDescription className="text-center text-gray-600">
                Please provide a few more details to complete your registration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUserDetailsSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <div className="relative">
                    <Input
                      id="dateOfBirth"
                      type="date"
                      required
                      className="bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400 [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <select
                    id="gender"
                    required
                    className="w-full p-2 rounded-md bg-white/50 border-cyan-200 focus:border-cyan-400 focus:ring-cyan-400"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </div>

                {userDetailsError && <p className="text-sm text-red-500">{userDetailsError}</p>}

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span>Completing Registration...</span>
                    </>
                  ) : (
                    "Complete Registration"
                  )}
                </Button>
              </form>
              {registrationSuccess && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 z-10 p-6 text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-green-500 mb-4"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </motion.div>
                  <h3 className="text-2xl font-bold text-green-600 mb-2">Registration Successful!</h3>
                  <p className="text-gray-700 mb-4">Your account has been created successfully.</p>
                  <p className="text-sm text-gray-500">You'll be redirected to login in a moment...</p>
                  <motion.div className="w-full max-w-xs bg-gray-200 h-2 mt-4 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-green-500"
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 3 }}
                    />
                  </motion.div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* {isResetPassword && (
        <div className="fixed inset-0 bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md bg-white/90 backdrop-blur-md shadow-xl relative overflow-hidden">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-center">Reset Password</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
                onClick={() => setIsResetPassword(false)}
              >
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </CardHeader>
            <CardContent>
              <ResetPasswordForm 
                token={resetToken} 
                onSuccess={() => {
                  setIsResetPassword(false);
                  alert('Password reset successfully! Please login with your new password.');
                }} 
              />
            </CardContent>
          </Card>
        </div>
      )} */}
    </div>
  )
}
